import { BooleanInput } from '@angular/cdk/coercion';
import { NgClass, NgIf } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnDestroy,
    OnInit,
    ViewEncapsulation,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { Router, RouterLink } from '@angular/router';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { CurrentUserService } from 'app/core/user/current-user.service';
import { User } from 'app/core/user/user.types';
import { Subject, takeUntil } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { ChangeCompanyComponent } from 'app/layout/common/user/change-company/change-company.component';
import { LanguagesComponent } from '../languages/languages.component';
import { ProfileProgressBarComponent } from "app/modules/admin/common/profile-progress-bar/profile-progress-bar.component";
import { UserService } from 'app/modules/admin/users/user.service';
import { AgreementsUrlHelper } from '../helpers/agreements-url.helper';

@Component({
    selector: 'user',
    templateUrl: './user.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs: 'user',
    standalone: true,
    imports: [
    MatButtonModule,
    MatMenuModule,
    MatIconModule,
    NgClass,
    MatDividerModule,
    TranslocoPipe,
    RouterLink,
    NgIf,
    LanguagesComponent,
    ProfileProgressBarComponent
],
})
export class UserComponent implements OnInit, OnDestroy {
    /* eslint-disable @typescript-eslint/naming-convention */
    static ngAcceptInputType_showAvatar: BooleanInput;
    /* eslint-enable @typescript-eslint/naming-convention */

    @Input() showAvatar: boolean = true;
    user: User;
    companyName: string;
    userInitial: string = '?';

    private _unsubscribeAll: Subject<any> = new Subject<any>();

    /**
     * Constructor
     */
    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _router: Router,
        private _matDialog: MatDialog,
        private _userService: CurrentUserService,
        private userService: UserService,
        private _translocoService: TranslocoService,
        private _agreementsUrlHelper: AgreementsUrlHelper
    ) {}

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Subscribe to user changes
        this._userService.user$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((user: User) => {
                this.user = user;

                // Buscar la compañía activa según el companyId del usuario
                if (user?.companies?.length && user.companyId) {
                    const activeCompany = user.companies.find(c => c.id === user.companyId);
                    this.companyName = activeCompany ? activeCompany.name : '';
                } else {
                    this.companyName = user?.companies?.length ? user.companies[0].name : '';
                }
                this.userInitial = this._computeInitial(user);
                // Mark for check
                this._changeDetectorRef.markForCheck();
            });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * help center
     */
    goToHelpCenter(): void {
        this._router.navigate(['/help-center']);
    }
    /**
     * privacy policy
     */
    goToPrivacyPolicy(): void {
        const url = this._agreementsUrlHelper.getPrivacyPolicyUrl(this._translocoService.getActiveLang());
        window.open(url, '_blank');
    }
    /**
     * data protection
    */
   goToTermsAndConditions(): void {
        const url = this._agreementsUrlHelper.getTermsAndConditionsUrl(this._translocoService.getActiveLang());
        window.open(url, '_blank');
    }
    /**
     * Sign out
     */
    signOut(): void {
        this._router.navigate(['/sign-out']);
    }

    /**
     * Display the change company component.
     */
    openChangeCompany() {
        this._matDialog.open(ChangeCompanyComponent, {
            autoFocus: false,
            data     : {
                note: {},
            },
        });
    }

    private _computeInitial(user: User): string {
        if (!user) {
            return '?';
        }

        const nameCandidates: (string | undefined)[] = [
            (user as any).name,
            (user as any).fullName,
            (user as any).firstName && (user as any).lastName
                ? `${(user as any).firstName} ${(user as any).lastName}`
                : (user as any).firstName,
        ];

        let base: string | undefined = nameCandidates.find(v => typeof v === 'string' && v.trim().length > 0);

        if (!base || !base.trim().length) {
            base = user.email || '';
        }

        base = base.trim();

        if (!base.length) {
            return '?';
        }

        if (base.includes('@')) {
            base = base.split('@')[0];
        }

        const firstChar = base
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .replace(/^[^a-zA-Z0-9]+/, '') 
            .charAt(0);

        return firstChar ? firstChar.toUpperCase() : '?';
    }

    changeCompanyEnabled()
    {
        return this.user?.companies?.length > 1;
    }
}
